/**
 * DOCUMENT INDEXER - Indexación de documentos con embeddings
 * 
 * Gestiona la indexación de documentos para búsqueda vectorial:
 * - Genera embeddings para documentos nuevos
 * - Actualiza embeddings cuando cambia el contenido
 * - Proporciona estado de indexación por colección
 * 
 * COLECCIONES SOPORTADAS:
 * - therapist_notes / therapist_note_chunks
 * - school_practices
 * - user_practice_library
 * - knowledge_base_documents
 */

import { db, FieldValue } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import { embeddingService } from './embedding-service';
import {
    IndexableDocument,
    IndexedDocument,
    IndexingResult,
    BatchIndexingResult,
    CollectionIndexStatus,
    EmbeddingModel,
    DEFAULT_EMBEDDING_MODEL,
} from './types';

const logger = Logger.getLogger('DocumentIndexer');

// ============================================
// CONFIGURACIÓN
// ============================================

const BATCH_SIZE = 10; // Documentos por batch
const MAX_CONTENT_LENGTH = 8000; // Límite para generar embedding

// Campos de contenido por colección
const CONTENT_FIELDS: Record<string, { title: string; content: string }> = {
    'therapist_notes': { title: 'title', content: 'extractedText' },
    'therapist_note_chunks': { title: 'noteTitle', content: 'content' },
    'school_practices': { title: 'name', content: 'description' },
    'user_practice_library': { title: 'title', content: 'description' },
    'knowledge_base_documents': { title: 'title', content: 'content' },
};

// ============================================
// DOCUMENT INDEXER SERVICE
// ============================================

class DocumentIndexerService {
    private readonly model: EmbeddingModel = DEFAULT_EMBEDDING_MODEL;

    /**
     * Indexar un documento individual
     */
    async indexDocument(doc: IndexableDocument): Promise<IndexingResult> {
        const startTime = Date.now();

        try {
            // Preparar texto para embedding
            const textToEmbed = this.prepareTextForEmbedding(doc);

            if (!textToEmbed || textToEmbed.length < 10) {
                return {
                    documentId: doc.id,
                    success: false,
                    error: 'Content too short for embedding',
                    processingTime: Date.now() - startTime,
                };
            }

            // Generar embedding
            const result = await embeddingService.generateEmbedding({
                text: textToEmbed,
                model: this.model,
            });

            // Calcular hash del contenido
            const contentHash = this.hashContent(textToEmbed);

            // Actualizar documento en Firestore
            await db.collection(doc.collectionName).doc(doc.id).update({
                embedding: result.embedding,
                embeddingModel: this.model,
                embeddingDimensions: result.dimensions,
                contentHash,
                indexedAt: new Date().toISOString(),
                hasEmbedding: true,
            });

            logger.debug('Document indexed', {
                documentId: doc.id,
                collection: doc.collectionName,
                dimensions: result.dimensions,
            });

            return {
                documentId: doc.id,
                success: true,
                processingTime: Date.now() - startTime,
            };

        } catch (error: any) {
            logger.error('Failed to index document', error, { documentId: doc.id });

            return {
                documentId: doc.id,
                success: false,
                error: error.message,
                processingTime: Date.now() - startTime,
            };
        }
    }

    /**
     * Indexar múltiples documentos en batch
     */
    async indexBatch(documents: IndexableDocument[]): Promise<BatchIndexingResult> {
        const startTime = Date.now();
        const results: IndexingResult[] = [];
        let successful = 0;
        let failed = 0;

        // Procesar en batches
        for (let i = 0; i < documents.length; i += BATCH_SIZE) {
            const batch = documents.slice(i, i + BATCH_SIZE);

            const batchResults = await Promise.allSettled(
                batch.map(doc => this.indexDocument(doc))
            );

            for (const result of batchResults) {
                if (result.status === 'fulfilled') {
                    results.push(result.value);
                    if (result.value.success) successful++;
                    else failed++;
                } else {
                    failed++;
                    results.push({
                        documentId: 'unknown',
                        success: false,
                        error: result.reason?.message || 'Unknown error',
                        processingTime: 0,
                    });
                }
            }

            // Pausa entre batches para no saturar
            if (i + BATCH_SIZE < documents.length) {
                await this.sleep(100);
            }
        }

        const totalTime = Date.now() - startTime;

        logger.info('Batch indexing completed', {
            total: documents.length,
            successful,
            failed,
            totalTime: `${totalTime}ms`,
        });

        return { successful, failed, results, totalTime };
    }

    /**
     * Indexar todos los documentos sin embedding de una colección
     */
    async indexCollection(
        collectionName: string,
        ownerId?: string,
        limit: number = 100
    ): Promise<BatchIndexingResult> {
        const ownerField = this.getOwnerField(collectionName);
        const contentFields = CONTENT_FIELDS[collectionName];

        if (!contentFields) {
            throw new Error(`Collection ${collectionName} not supported for indexing`);
        }

        // Query documentos sin embedding
        let query: any = db.collection(collectionName)
            .where('hasEmbedding', '!=', true)
            .limit(limit);

        if (ownerId && ownerField) {
            query = query.where(ownerField, '==', ownerId);
        }

        const snapshot = await query.get();

        if (snapshot.empty) {
            logger.info('No documents to index', { collectionName, ownerId });
            return {
                successful: 0,
                failed: 0,
                results: [],
                totalTime: 0,
            };
        }

        // Convertir a IndexableDocument
        const documents: IndexableDocument[] = snapshot.docs.map((doc: any) => {
            const data = doc.data();
            return {
                id: doc.id,
                collectionName,
                title: data[contentFields.title] || '',
                content: data[contentFields.content] || '',
                metadata: data,
            };
        });

        logger.info('Starting collection indexing', {
            collectionName,
            documentsToIndex: documents.length,
        });

        return this.indexBatch(documents);
    }

    /**
     * Re-indexar documento si el contenido cambió
     */
    async reindexIfChanged(doc: IndexableDocument, currentHash?: string): Promise<boolean> {
        const textToEmbed = this.prepareTextForEmbedding(doc);
        const newHash = this.hashContent(textToEmbed);

        if (currentHash && currentHash === newHash) {
            logger.debug('Content unchanged, skipping reindex', { documentId: doc.id });
            return false;
        }

        const result = await this.indexDocument(doc);
        return result.success;
    }

    /**
     * Obtener estado de indexación de una colección
     */
    async getCollectionStatus(
        collectionName: string,
        ownerId?: string
    ): Promise<CollectionIndexStatus> {
        const ownerField = this.getOwnerField(collectionName);

        // Query total documentos
        let totalQuery: any = db.collection(collectionName);
        if (ownerId && ownerField) {
            totalQuery = totalQuery.where(ownerField, '==', ownerId);
        }

        // Query documentos indexados
        let indexedQuery = totalQuery.where('hasEmbedding', '==', true);

        const [totalSnapshot, indexedSnapshot] = await Promise.all([
            totalQuery.get(),
            indexedQuery.get(),
        ]);

        const totalDocuments = totalSnapshot.size;
        const indexedDocuments = indexedSnapshot.size;

        return {
            collectionName,
            totalDocuments,
            indexedDocuments,
            completionPercentage: totalDocuments > 0
                ? Math.round((indexedDocuments / totalDocuments) * 100)
                : 100,
            lastUpdatedAt: new Date().toISOString(),
            embeddingModel: this.model,
        };
    }

    /**
     * Eliminar embedding de un documento
     */
    async removeEmbedding(collectionName: string, documentId: string): Promise<void> {
        await db.collection(collectionName).doc(documentId).update({
            embedding: FieldValue.delete(),
            embeddingModel: FieldValue.delete(),
            embeddingDimensions: FieldValue.delete(),
            contentHash: FieldValue.delete(),
            indexedAt: FieldValue.delete(),
            hasEmbedding: false,
        });

        logger.debug('Embedding removed', { collectionName, documentId });
    }

    // ============================================
    // UTILIDADES PRIVADAS
    // ============================================

    private prepareTextForEmbedding(doc: IndexableDocument): string {
        const parts: string[] = [];

        // Título tiene más peso
        if (doc.title) {
            parts.push(`Título: ${doc.title}`);
        }

        // Contenido principal
        if (doc.content) {
            parts.push(doc.content);
        }

        const combined = parts.join('\n\n');

        // Truncar si es muy largo
        return combined.length > MAX_CONTENT_LENGTH
            ? combined.substring(0, MAX_CONTENT_LENGTH)
            : combined;
    }

    private hashContent(content: string): string {
        // Hash simple basado en longitud y primeros/últimos caracteres
        const start = content.substring(0, 50);
        const end = content.substring(content.length - 50);
        return `${content.length}_${this.simpleHash(start + end)}`;
    }

    private simpleHash(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(36);
    }

    private getOwnerField(collectionName: string): string | null {
        const ownerFields: Record<string, string> = {
            'therapist_notes': 'therapistId',
            'therapist_note_chunks': 'therapistId',
            'school_practices': 'schoolId',
            'user_practice_library': 'userId',
            'knowledge_base_documents': 'ownerId',
        };
        return ownerFields[collectionName] || null;
    }

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// Singleton export
export const documentIndexer = new DocumentIndexerService();
export default documentIndexer;
